import { coloredGeometry, loadBackyard } from "./backyard";
import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { addRimLight, createToonMaterial } from "./snowman";

export type Team = "player" | "opponent";
export const TEAM_COLORS = { player: 0x2366ff, opponent: 0xff5a4e } as const;

const loader = new GLTFLoader();

export function applyTeamColor(root: THREE.Object3D, team: Team): void {
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const mesh = object as THREE.Mesh<
      THREE.BufferGeometry,
      THREE.Material | THREE.Material[]
    >;
    const sourceMaterial = mesh.material;
    const materials: THREE.Material[] = Array.isArray(sourceMaterial)
      ? sourceMaterial
      : [sourceMaterial];
    const colored: THREE.Material[] = materials.map((source) => {
      const material = source.clone();
      if (material.name === "mat_team") {
        (material as THREE.MeshStandardMaterial).color.setHex(
          TEAM_COLORS[team],
        );
      }
      return material;
    });
    mesh.material = colored.length === 1 ? colored[0]! : colored;
  });
}

function toonify(
  root: THREE.Object3D,
  outlined: boolean,
  outlineRatio = 0.03,
): void {
  const meshes: THREE.Mesh[] = [];
  root.traverse((object) => {
    if (object instanceof THREE.Mesh) meshes.push(object as THREE.Mesh);
  });
  for (const mesh of meshes) {
    const originals = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];
    const converted = originals.map((source) => {
      if (!(source instanceof THREE.MeshStandardMaterial)) return source;
      const toon = createToonMaterial(source.color);
      toon.name = source.name;
      toon.map = source.map;
      toon.vertexColors = source.vertexColors;
      toon.transparent = source.transparent;
      toon.opacity = source.opacity;
      toon.depthWrite = source.depthWrite;
      addRimLight(toon);
      return toon;
    });
    mesh.material = converted.length === 1 ? converted[0]! : converted;
    mesh.renderOrder = Number(mesh.userData.renderOrder ?? mesh.renderOrder);
    if (mesh.name.startsWith("face_")) {
      const faceMaterials = Array.isArray(mesh.material)
        ? mesh.material
        : [mesh.material];
      for (const material of faceMaterials) {
        material.polygonOffset = true;
        material.polygonOffsetFactor = -1;
        material.polygonOffsetUnits = -1;
        material.depthWrite = true;
      }
    }
    if (!outlined || mesh.name.startsWith("face_")) continue;
    mesh.geometry.computeBoundingSphere();
    const distance = (mesh.geometry.boundingSphere?.radius ?? 1) * outlineRatio;
    const geometry = mesh.geometry.clone();
    const position = geometry.getAttribute("position");
    const normal = geometry.getAttribute("normal");
    if (position && normal) {
      for (let i = 0; i < position.count; i++) {
        position.setXYZ(
          i,
          position.getX(i) + normal.getX(i) * distance,
          position.getY(i) + normal.getY(i) * distance,
          position.getZ(i) + normal.getZ(i) * distance,
        );
      }
    }
    const hull = new THREE.Mesh(
      geometry,
      new THREE.MeshBasicMaterial({
        color: 0x4b4a8c,
        side: THREE.BackSide,
        transparent: true,
        opacity: 0.6,
        depthWrite: false,
      }),
    );
    hull.name = `${mesh.name}_outline`;
    hull.renderOrder = mesh.renderOrder - 0.5;
    mesh.add(hull);
  }
}

async function load(path: string): Promise<THREE.Group> {
  return (await loader.loadAsync(`/art/models/${path}`)).scene;
}

export function attachAtAnchor(
  base: THREE.Object3D,
  anchorName: string,
  part: THREE.Object3D,
): void {
  const anchor = base.getObjectByName(anchorName);
  if (!anchor) throw new Error(`Missing snowman anchor: ${anchorName}`);
  anchor.add(part);
}

export interface ArtSnowman {
  root: THREE.Group;
  head: THREE.Object3D;
}

export async function loadArtSnowman(
  team: Team,
  lod1 = false,
  loadModel: (path: string) => Promise<THREE.Group> = load,
): Promise<ArtSnowman> {
  const prefix = lod1 ? "lod1/" : "";
  const [base, faces, beanie, scarf, leftMitten, rightMitten] =
    await Promise.all([
      loadModel(`${prefix}snowman_base.glb`),
      loadModel(`${prefix}faces.glb`),
      loadModel(`${prefix}beanie.glb`),
      loadModel(`${prefix}scarf.glb`),
      loadModel(`${prefix}mitten.glb`),
      loadModel(`${prefix}mitten.glb`),
    ]);
  const happy = faces.getObjectByName("face_happy");
  if (happy) {
    const grin = happy.clone();
    grin.name = "face_grin";
    happy.parent?.add(grin);
  }
  const shocked = new THREE.Group();
  for (const [x, y, z, r, color, ring] of [
    [-0.078, -0.026, 0.325, 0.017, 0x2a2440, 0],
    [0.078, -0.026, 0.325, 0.017, 0x2a2440, 0],
    [0, -0.103, 0.327, 0.034, 0x2a2440, 1],
    [-0.16, -0.105, 0.285, 0.025, 0xffa4c5, 0],
    [0.16, -0.105, 0.285, 0.025, 0xffa4c5, 0],
  ]) {
    const mesh = new THREE.Mesh(
      ring
        ? new THREE.RingGeometry(r! * 0.6, r, 16)
        : new THREE.CircleGeometry(r, 16),
      new THREE.MeshBasicMaterial({ color }),
    );
    mesh.position.set(x!, y!, z!);
    shocked.add(mesh);
  }
  const shockedMesh = new THREE.Mesh(
    coloredGeometry(shocked),
    new THREE.MeshBasicMaterial({ vertexColors: true }),
  );
  shockedMesh.name = "face_shocked";
  (happy?.parent ?? faces).add(shockedMesh);
  const sad = new THREE.Group();
  sad.name = "face_sad";
  for (const x of [-0.078, 0.078]) {
    const eye = new THREE.Mesh(
      new THREE.CircleGeometry(0.017, 12),
      new THREE.MeshBasicMaterial({ color: 0x2a2440 }),
    );
    eye.position.set(x, -0.026, 0.325);
    sad.add(eye);
  }
  const mouth = new THREE.Mesh(
    new THREE.RingGeometry(0.026, 0.035, 16, 1, 0, Math.PI),
    new THREE.MeshBasicMaterial({ color: 0x2a2440, side: THREE.DoubleSide }),
  );
  mouth.position.set(0, -0.12, 0.33);
  sad.add(mouth);
  (happy?.parent ?? faces).add(sad);
  for (const part of [base, faces, beanie, scarf, leftMitten, rightMitten]) {
    applyTeamColor(part, team);
    toonify(part, part !== faces);
  }
  faces.traverse((node) => {
    if (node.name.startsWith("face_"))
      node.visible = node.name === "face_determined";
  });
  const builtInFace = base.getObjectByName("face_dot");
  if (builtInFace) builtInFace.visible = false;
  attachAtAnchor(base, "anchor_face", faces);
  attachAtAnchor(base, "anchor_hat", beanie);
  attachAtAnchor(base, "anchor_scarf", scarf);
  attachAtAnchor(base, "anchor_mitten_L", leftMitten);
  rightMitten.scale.x = -1;
  attachAtAnchor(base, "anchor_mitten_R", rightMitten);
  base.name = `${team}_snowman`;
  return { root: base, head: base.getObjectByName("head") ?? base };
}

export async function loadArtSnowball(
  size: "S" | "M" | "L",
): Promise<THREE.Group> {
  const root = await load("snowball.glb");
  const scales = { S: 0.175, M: 0.275, L: 0.4 } as const;
  root.scale.setScalar(scales[size]);
  root.traverse((node) => {
    if (node.name === "band_mid") node.visible = size === "M";
    if (["band_upper", "band_lower", "sparkle"].includes(node.name))
      node.visible = size === "L";
  });
  toonify(root, true, 0.08);
  root.traverse((node) => {
    if (node instanceof THREE.Mesh && node.name.endsWith("_outline")) {
      node.visible = node.name === "snowball_outline";
      const material = node.material as THREE.MeshBasicMaterial;
      material.color.setHex(0x1b1f4a);
      material.opacity = 1;
    }
  });
  return root;
}

export async function loadArena() {
  return loadBackyard(load);
}

export function loadPodium() {
  return load("props_v2/podium.glb");
}
